import {
     CONST_CLIENT_BON_LIVRAISON_ETAT_ENVOYE_AU_CLIENT,
     CONST_CLIENT_BON_LIVRAISON_ETAT_RECU_AVEC_SIGNATURE_CLIENT,
     CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR,
} from "@common-constants/*"
import { IAddBonLivraisonToBonCommandeRequestModel, IExtractHtTvaFromInvoiceResponseModel } from "../core/_models"
import { Form, Formik, FormikHelpers } from "formik"
import { IClientBonCommandeModel, IHttpErrorResponseModel } from "@common-models/*"
import { toast } from "react-toastify"
import { AxiosError } from "axios"
import { addBonLivraisonToBonCommandeRequest, extractHtTvaFromInvoiceRequest } from "../core/_requests"
import MyAlert from "@common-utils/MyAlert"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import React, { useCallback, useEffect, useRef, useState } from "react"
import MyDateDayMonthYearField from "@common-utils/fields/MyDateDayMonthYearField"
import MySelectField from "@common-utils/fields/MySelectField"
import MyInputField from "@common-utils/fields/MyInputField"
import * as Yup from "yup"
import Split from "react-split-it"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseExtraction(data: IExtractHtTvaFromInvoiceResponseModel): { montantHT?: number; montantTVA?: number } {
     const invoice = data?.invoices?.[0]
     return { montantHT: invoice?.totalAmountHt, montantTVA: invoice?.totalVat }
}

const ACCEPTED_MIME = ["application/pdf", "image/jpeg", "image/jpg", "image/png"]
const ACCEPTED_LABEL = "PDF, JPEG ou PNG"

const validationSchema = Yup.object().shape({
     dateDebut: Yup.string().required("La date de début est requise"),
     dateFin: Yup.string().required("La date de fin est requise"),
     montantHT: Yup.number().typeError("Le montant HT doit être un nombre").required("Le montant HT est requis"),
     montantTVA: Yup.number().typeError("Le montant TVA doit être un nombre").required("Le montant TVA est requis"),
     etat: Yup.string().required("L'état est requis"),
     document: Yup.mixed().required("Le document est requis"),
})

// ---------------------------------------------------------------------------
// Dropzone
// ---------------------------------------------------------------------------

type DropzoneState = "idle" | "scanning" | "done" | "error"

interface DropzoneProps {
     onFile: (file: File) => void
     state: DropzoneState
     file: File | undefined
     onReset: () => void
}

const Dropzone = ({ onFile, state, file, onReset }: DropzoneProps) => {
     const inputRef = useRef<HTMLInputElement>(null)
     const [isDraggingOver, setIsDraggingOver] = useState(false)

     const pick = useCallback(
          (f: File) => {
               if (!ACCEPTED_MIME.includes(f.type)) {
                    toast.error(`Format non supporté. Veuillez déposer un ${ACCEPTED_LABEL}.`)
                    return
               }
               onFile(f)
          },
          [onFile]
     )

     const onDrop = useCallback(
          (e: React.DragEvent) => {
               e.preventDefault()
               setIsDraggingOver(false)
               const f = e.dataTransfer.files?.[0]
               if (f) pick(f)
          },
          [pick]
     )

     const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const f = e.target.files?.[0]
          if (f) pick(f)
          e.target.value = ""
     }

     const isInteractive = state === "idle" || state === "error"

     const borderColor = isDraggingOver ? "#0d6efd" : state === "scanning" ? "#0d6efd" : state === "done" ? "#198754" : state === "error" ? "#dc3545" : "#ced4da"

     const bgColor = isDraggingOver
          ? "rgba(13,110,253,0.06)"
          : state === "scanning"
          ? "rgba(13,110,253,0.03)"
          : state === "done"
          ? "rgba(25,135,84,0.04)"
          : state === "error"
          ? "rgba(220,53,69,0.04)"
          : "#fafafa"

     const containerStyle: React.CSSProperties = {
          position: "relative",
          border: `2px dashed ${borderColor}`,
          borderRadius: 12,
          background: bgColor,
          transition: "border-color 0.2s, background 0.2s",
          cursor: isInteractive ? "pointer" : "default",
          overflow: "hidden",
     }

     const scannerStyle: React.CSSProperties = {
          position: "absolute",
          inset: 0,
          background: "linear-gradient(180deg, transparent 0%, rgba(0,123,255,0.08) 50%, transparent 100%)",
          backgroundSize: "100% 200%",
          animation: "bl-scan 1.6s ease-in-out infinite",
          pointerEvents: "none",
          borderRadius: "inherit",
     }

     return (
          <>
               <style>{`
                    @keyframes bl-scan { 0% { background-position: 0 -100%; } 100% { background-position: 0 200%; } }
                    @keyframes bl-spin { to { transform: rotate(360deg); } }
               `}</style>

               <div
                    style={containerStyle}
                    onClick={() => isInteractive && inputRef.current?.click()}
                    onDragOver={e => {
                         e.preventDefault()
                         if (isInteractive) setIsDraggingOver(true)
                    }}
                    onDragLeave={() => setIsDraggingOver(false)}
                    onDrop={isInteractive ? onDrop : undefined}
               >
                    {state === "scanning" && <div style={scannerStyle} />}

                    <div className="d-flex flex-column align-items-center justify-content-center gap-3 py-5 px-4" style={{ minHeight: 180 }}>
                         {/* IDLE */}
                         {state === "idle" && (
                              <>
                                   <div
                                        style={{
                                             width: 52,
                                             height: 52,
                                             borderRadius: "50%",
                                             background: isDraggingOver ? "rgba(13,110,253,0.12)" : "rgba(13,110,253,0.06)",
                                             display: "flex",
                                             alignItems: "center",
                                             justifyContent: "center",
                                             transition: "background 0.2s",
                                        }}
                                   >
                                        <i className="fas fa-cloud-upload-alt text-primary" style={{ fontSize: 22 }} />
                                   </div>
                                   <div className="text-center">
                                        <p className="fw-semibold text-gray-700 mb-1" style={{ fontSize: 15 }}>
                                             {isDraggingOver ? "Déposez le fichier ici" : "Glissez-déposez votre BL ici"}
                                        </p>
                                        <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                                             ou <span className="text-primary fw-semibold">parcourez</span> vos fichiers · {ACCEPTED_LABEL} · max 10 Mo
                                        </p>
                                   </div>
                              </>
                         )}

                         {/* SCANNING */}
                         {state === "scanning" && (
                              <>
                                   <div
                                        style={{
                                             width: 52,
                                             height: 52,
                                             borderRadius: "50%",
                                             background: "rgba(13,110,253,0.08)",
                                             display: "flex",
                                             alignItems: "center",
                                             justifyContent: "center",
                                        }}
                                   >
                                        <i className="fas fa-search text-primary" style={{ fontSize: 20, animation: "bl-spin 2s linear infinite" }} />
                                   </div>
                                   <div className="text-center">
                                        <p className="fw-semibold text-gray-700 mb-1" style={{ fontSize: 15 }}>
                                             Analyse en cours…
                                        </p>
                                        <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                                             <i className={`fas fa-file-${file?.type === "application/pdf" ? "pdf text-danger" : "image text-primary"} me-1`} />
                                             {file?.name}
                                        </p>
                                   </div>
                                   <div style={{ width: "60%", height: 4, background: "rgba(13,110,253,0.15)", borderRadius: 99, overflow: "hidden" }}>
                                        <div style={{ height: "100%", width: "40%", background: "#0d6efd", borderRadius: 99, animation: "bl-scan 1.6s ease-in-out infinite" }} />
                                   </div>
                              </>
                         )}

                         {/* DONE */}
                         {state === "done" && file && (
                              <>
                                   <div
                                        style={{
                                             width: 52,
                                             height: 52,
                                             borderRadius: "50%",
                                             background: "rgba(25,135,84,0.08)",
                                             display: "flex",
                                             alignItems: "center",
                                             justifyContent: "center",
                                        }}
                                   >
                                        <i className="fas fa-check-circle text-success" style={{ fontSize: 22 }} />
                                   </div>
                                   <div className="text-center">
                                        <p className="fw-semibold text-gray-700 mb-1" style={{ fontSize: 15 }}>
                                             Document analysé
                                        </p>
                                        <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                                             <i className={`fas fa-file-${file.type === "application/pdf" ? "pdf text-danger" : "image text-primary"} me-1`} />
                                             {file.name}
                                        </p>
                                   </div>
                                   <button
                                        type="button"
                                        className="btn btn-sm btn-light-danger"
                                        onClick={e => {
                                             e.stopPropagation()
                                             onReset()
                                        }}
                                   >
                                        <i className="fas fa-times me-1" /> Supprimer
                                   </button>
                              </>
                         )}

                         {/* ERROR */}
                         {state === "error" && (
                              <>
                                   <div
                                        style={{
                                             width: 52,
                                             height: 52,
                                             borderRadius: "50%",
                                             background: "rgba(220,53,69,0.08)",
                                             display: "flex",
                                             alignItems: "center",
                                             justifyContent: "center",
                                        }}
                                   >
                                        <i className="fas fa-exclamation-triangle text-danger" style={{ fontSize: 22 }} />
                                   </div>
                                   <div className="text-center">
                                        <p className="fw-semibold text-danger mb-1" style={{ fontSize: 15 }}>
                                             L'analyse a échoué
                                        </p>
                                        <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                                             Cliquez pour sélectionner un autre fichier, ou remplissez les montants manuellement
                                        </p>
                                   </div>
                                   <button
                                        type="button"
                                        className="btn btn-sm btn-light-danger"
                                        onClick={e => {
                                             e.stopPropagation()
                                             onReset()
                                        }}
                                   >
                                        <i className="fas fa-redo me-1" /> Réessayer
                                   </button>
                              </>
                         )}
                    </div>

                    <input ref={inputRef} type="file" accept={ACCEPTED_MIME.join(",")} style={{ display: "none" }} onChange={onInputChange} />
               </div>
          </>
     )
}

// ---------------------------------------------------------------------------
// Document preview pane
// ---------------------------------------------------------------------------

const DocumentPreview = ({ file }: { file: File }) => {
     const [objectUrl, setObjectUrl] = useState<string | null>(null)
     const [isLoading, setIsLoading] = useState(true)
     const isPdf = file.type === "application/pdf"

     useEffect(() => {
          const url = URL.createObjectURL(file)
          setObjectUrl(url)
          setIsLoading(true)
          return () => URL.revokeObjectURL(url)
     }, [file])

     if (!objectUrl) return null

     return (
          <div className="position-relative w-100" style={{ height: "82vh" }}>
               {/* Loading overlay */}
               {isLoading && (
                    <div className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center bg-white top-0 start-0" style={{ zIndex: 1 }}>
                         <MySimpleSpinner size="lg" />
                    </div>
               )}

               {/* File name + open link */}
               <div className="d-flex align-items-center justify-content-between px-2 pb-2">
                    <span className="fw-semibold text-muted" style={{ fontSize: 13 }}>
                         <i className={`fas ${isPdf ? "fa-file-pdf text-danger" : "fa-file-image text-primary"} me-1`} />
                         {file.name}
                    </span>
                    <a href={objectUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-light-primary py-1 px-2" style={{ fontSize: 12 }}>
                         <i className="fas fa-external-link-alt me-1" /> Ouvrir
                    </a>
               </div>

               {isPdf ? (
                    <iframe src={objectUrl} title="Aperçu du BL" style={{ width: "100%", height: "calc(82vh - 36px)", border: "none" }} onLoad={() => setIsLoading(false)} />
               ) : (
                    <img
                         src={objectUrl}
                         alt="Aperçu du BL"
                         style={{ width: "100%", height: "calc(82vh - 36px)", objectFit: "contain", background: "#f5f5f5", display: "block" }}
                         onLoad={() => setIsLoading(false)}
                    />
               )}
          </div>
     )
}

// ---------------------------------------------------------------------------
// Main form
// ---------------------------------------------------------------------------

const PtfPeriodeBonCommandeAddBonLivraisonForm = ({ bc_ID, dateDebut, dateFin, handleSubmitCallback = () => {} }: IPtfPeriodeBonCommandeAddBonLivraisonFormPropsModel) => {
     const [isXlScreen, setIsXlScreen] = useState<boolean>(window.innerWidth >= 1200)
     const [selectedTabMobile, setSelectedTabMobile] = useState<"form" | "preview">("form")

     const [dropzoneState, setDropzoneState] = useState<DropzoneState>("idle")
     const [scannedDocument, setScannedDocument] = useState<File | undefined>(undefined)
     const [extraction, setExtraction] = useState<{ montantHT?: number; montantTVA?: number }>({})

     const showPreview = dropzoneState === "done" && !!scannedDocument
     const showForm = dropzoneState === "done" || dropzoneState === "error"

     useEffect(() => {
          const handleResize = () => setIsXlScreen(window.innerWidth >= 1200)
          window.addEventListener("resize", handleResize)
          return () => window.removeEventListener("resize", handleResize)
     }, [])

     const initialValues: IAddBonLivraisonToBonCommandeRequestModel = {
          etat: CONST_CLIENT_BON_LIVRAISON_ETAT_ENVOYE_AU_CLIENT,
          dateDebut,
          dateFin,
          document: scannedDocument,
          montantHT: extraction.montantHT,
          montantTVA: extraction.montantTVA,
     }

     function handleFile(file: File) {
          setScannedDocument(file)
          setExtraction({})
          setDropzoneState("scanning")

          extractHtTvaFromInvoiceRequest(file)
               .then(r => {
                    setExtraction(parseExtraction(r.data))
                    setDropzoneState("done")
               })
               .catch(() => {
                    toast.error("L'extraction a échoué, veuillez saisir les montants manuellement", { autoClose: false })
                    setDropzoneState("error")
               })
     }

     function handleReset() {
          setScannedDocument(undefined)
          setExtraction({})
          setDropzoneState("idle")
          setSelectedTabMobile("form")
     }

     function handleSubmit(values: IAddBonLivraisonToBonCommandeRequestModel, helpers: FormikHelpers<IAddBonLivraisonToBonCommandeRequestModel>) {
          helpers.setStatus(null)

          addBonLivraisonToBonCommandeRequest(bc_ID, values)
               .then(r => {
                    toast.success("Le bon de livraison a bien été ajouté.")
                    handleSubmitCallback(r.data)
               })
               .catch((e: AxiosError) => {
                    const error: IHttpErrorResponseModel = e.response?.data

                    if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                         for (const key in error.errors) helpers.setFieldError(key, error.errors[key])
                    }

                    helpers.setStatus(error?.detail)
                    toast.error(error?.detail, { autoClose: false })
                    helpers.setSubmitting(false)
               })
               .finally(() => helpers.setSubmitting(false))
     }

     // ── Left pane: dropzone + form ────────────────────────────────────────
     const leftPane = (
          <div className="d-flex flex-column gap-4">
               <Dropzone state={dropzoneState} file={scannedDocument} onFile={handleFile} onReset={handleReset} />

               {showForm && (
                    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
                         {helpers => (
                              <Form noValidate autoComplete="off">
                                   {helpers.status && (
                                        <MyAlert type={"danger"} classNames={"mb-4"}>
                                             {helpers.status}
                                        </MyAlert>
                                   )}

                                   {/* Période */}
                                   <div className="mb-4">
                                        <label className="form-label fw-bold fs-6 required">Période</label>
                                        <div className="d-flex">
                                             <MyDateDayMonthYearField
                                                  name={"dateDebut"}
                                                  value={helpers.values.dateDebut}
                                                  isInvalid={!!helpers.errors.dateDebut}
                                                  maxDate={helpers.values.dateFin}
                                                  onChange={val => helpers.setFieldValue("dateDebut", val)}
                                                  placeholder={"Date de début"}
                                                  classNames={"rounded-0 rounded-start"}
                                             />
                                             <span className="input-group-text fas fa-ellipsis d-flex align-items-center rounded-0" />
                                             <MyDateDayMonthYearField
                                                  name={"dateFin"}
                                                  value={helpers.values.dateFin}
                                                  minDate={helpers.values.dateDebut}
                                                  isInvalid={!!helpers.errors.dateFin}
                                                  onChange={val => helpers.setFieldValue("dateFin", val)}
                                                  placeholder={"Date de fin"}
                                                  classNames={"rounded-0 rounded-end"}
                                             />
                                        </div>
                                        {helpers.errors.dateDebut && <div className="text-danger">{helpers.errors.dateDebut}</div>}
                                        {helpers.errors.dateFin && <div className="text-danger">{helpers.errors.dateFin}</div>}
                                   </div>

                                   {/* Montant HT */}
                                   <div className="mb-4">
                                        <label className="form-label fw-bold fs-6 required">Montant HT</label>
                                        <MyInputField
                                             name={"montantHT"}
                                             value={helpers.values.montantHT}
                                             isInvalid={!!helpers.errors.montantHT}
                                             inputMode={"numeric"}
                                             type={"number"}
                                             onChange={val => helpers.setFieldValue("montantHT", val)}
                                             placeholder={"Montant HT"}
                                        />
                                        {helpers.errors.montantHT && <div className="text-danger">{helpers.errors.montantHT}</div>}
                                   </div>

                                   {/* Montant TVA */}
                                   <div className="mb-4">
                                        <label className="form-label fw-bold fs-6 required">Montant TVA</label>
                                        <MyInputField
                                             name={"montantTVA"}
                                             value={helpers.values.montantTVA}
                                             isInvalid={!!helpers.errors.montantTVA}
                                             inputMode={"numeric"}
                                             type={"number"}
                                             onChange={val => helpers.setFieldValue("montantTVA", val)}
                                             placeholder={"Montant TVA"}
                                        />
                                        {helpers.errors.montantTVA && <div className="text-danger">{helpers.errors.montantTVA}</div>}
                                   </div>

                                   {/* État */}
                                   <div className="mb-4">
                                        <label className="form-label fw-bold fs-6 required">État</label>
                                        <MySelectField
                                             name={"etat"}
                                             options={[
                                                  { value: CONST_CLIENT_BON_LIVRAISON_ETAT_ENVOYE_AU_CLIENT, label: "Envoyé au client" },
                                                  { value: CONST_CLIENT_BON_LIVRAISON_ETAT_RECU_AVEC_SIGNATURE_CLIENT, label: "Signé par le client" },
                                             ]}
                                             value={helpers.values.etat}
                                             isInvalid={!!helpers.errors.etat}
                                             onChange={val => helpers.setFieldValue("etat", val)}
                                        />
                                        {helpers.errors.etat && <div className="text-danger">{helpers.errors.etat}</div>}
                                   </div>

                                   {/* Submit */}
                                   <div className="d-flex justify-content-end">
                                        <button className="btn btn-sm btn-primary d-inline-flex align-items-center" type="submit" disabled={helpers.isSubmitting}>
                                             {!helpers.isSubmitting ? (
                                                  <>
                                                       {" "}
                                                       Ajouter <i className="fas fa-plus-circle fs-4 ms-1" />{" "}
                                                  </>
                                             ) : (
                                                  <MySimpleSpinner size={"sm"} />
                                             )}
                                        </button>
                                   </div>
                              </Form>
                         )}
                    </Formik>
               )}
          </div>
     )

     // ── Right pane: document preview ──────────────────────────────────────
     const rightPane = showPreview ? (
          <div className="ps-2 h-100">
               <DocumentPreview file={scannedDocument!} />
          </div>
     ) : null

     // ── Render ────────────────────────────────────────────────────────────
     return (
          <>
               {isXlScreen && showPreview ? (
                    // Desktop split view — only when there's something to preview
                    <Split sizes={[40, 60]} direction="horizontal" gutterSize={10}>
                         <div className="pe-3 overflow-auto" style={{ maxHeight: "82vh" }}>
                              {leftPane}
                         </div>
                         {rightPane}
                    </Split>
               ) : (
                    <>
                         {/* Mobile tab toggle — only visible once there's a preview */}
                         {showPreview && !isXlScreen && (
                              <div className="d-flex justify-content-center mb-4">
                                   <div className="btn-group">
                                        <button className={`btn btn-sm ${selectedTabMobile === "form" ? "btn-primary" : "btn-light"}`} onClick={() => setSelectedTabMobile("form")}>
                                             <i className="fas fa-edit me-2" />
                                             Formulaire
                                        </button>
                                        <button
                                             className={`btn btn-sm ${selectedTabMobile === "preview" ? "btn-primary" : "btn-light"}`}
                                             onClick={() => setSelectedTabMobile("preview")}
                                        >
                                             <i className="fas fa-file-pdf me-2" />
                                             Voir le document
                                        </button>
                                   </div>
                              </div>
                         )}

                         {/* On mobile always show left pane unless user switched to preview tab */}
                         {(!showPreview || selectedTabMobile === "form") && leftPane}
                         {showPreview && selectedTabMobile === "preview" && rightPane}
                    </>
               )}
          </>
     )
}

interface IPtfPeriodeBonCommandeAddBonLivraisonFormPropsModel {
     bc_ID: number
     dateDebut?: string
     dateFin?: string
     handleSubmitCallback?: (val: IClientBonCommandeModel) => void
}

export default PtfPeriodeBonCommandeAddBonLivraisonForm
